import { CascaderOptionType } from "antd/es/cascader";
import React from "react";
import { getDesignerSymbolPickerOptionName } from "../../../../shared/utility/designer-symbol-picker-engine";

/**
 * Test-ids on the symbol picker's cascader rows.
 *
 * Two ids per string-labelled row, on two nested spans that share the row's box:
 *
 *  · `symbolPickerOption-<label>` (INNER span) — the original id. The E2E suite walks the columns
 *    by it (`getByTestId(\`symbolPickerOption-${label}\`)`) and counts open popups with
 *    `getByTestId(/^symbolPickerOption-/)`, so its value and its prefix are frozen.
 *  · `symbolPickerOptionPath-<segment>/<segment>/…` (OUTER span) — unique per row: the chain of
 *    labels from the root column down to this row. A label alone is not unique ("value" sits under
 *    every component). The prefix is deliberately NOT `symbolPickerOption-`: a root row's path
 *    equals its label, so a shared prefix would give that row two identical ids (a Playwright
 *    strict-mode failure) and double every `/^symbolPickerOption-/` count.
 *
 * Built from labels, never from `option.value`: the engine assigns fresh GUIDs to every value each
 * time the popup opens (`setKeyOfNodeTree`).
 *
 * The label stays a single element whose `key` AND `props.children` are the label text — exactly
 * the shape the engine's readers expect (`getDesignerSymbolPickerOptionLabel` flattens children,
 * `getDesignerSymbolPickerOptionName` reads the key, `label.props.children.key` resolves to
 * `undefined` as it does for a string). Nesting a second element as `children` would change what
 * `buildSymbolFromSelectedOptions` writes into `ISymbol.description`; rendering both spans from a
 * component keeps that shape.
 */
export const SYMBOL_PICKER_OPTION_TEST_ID_PREFIX: string = "symbolPickerOption-";

export const SYMBOL_PICKER_OPTION_PATH_TEST_ID_PREFIX: string = "symbolPickerOptionPath-";

const PATH_SEPARATOR: string = "/";

interface ISymbolPickerOptionLabelProps {
  /** The full `symbolPickerOptionPath-…` id. */
  pathTestId: string;
  /** The label text. Kept as `children` on purpose — see the module comment. */
  children: string;
}

export const SymbolPickerOptionLabel: React.FC<ISymbolPickerOptionLabelProps> = ({
  pathTestId,
  children
}: ISymbolPickerOptionLabelProps) => (
  <span data-testid={pathTestId}>
    <span data-testid={`${SYMBOL_PICKER_OPTION_TEST_ID_PREFIX}${children}`}>{children}</span>
  </span>
);

/** One path segment: the name, trimmed, with whitespace and the separator folded to `_`. */
export function toSymbolPickerPathSegment(name: string): string {
  return name.trim().replace(/[\s/]+/g, "_");
}

/**
 * Segments for one column, in order. A name repeated among siblings gets `~2`, `~3`, … from its
 * second occurrence on, so every row of a column — and therefore every path — is distinct.
 */
function columnSegments(options: CascaderOptionType[]): string[] {
  const seen: Map<string, number> = new Map<string, number>();
  return options.map((option: CascaderOptionType): string => {
    const base: string = toSymbolPickerPathSegment(getDesignerSymbolPickerOptionName(option));
    const occurrence: number = (seen.get(base) ?? 0) + 1;
    seen.set(base, occurrence);
    return occurrence === 1 ? base : `${base}~${occurrence}`;
  });
}

/**
 * The rendered copy of the option tree with both test-ids on every string-labelled row. Pure: the
 * input tree is not touched, so `state.options` keeps its string labels. Options whose label is
 * already a ReactNode keep it (they still contribute a path segment to their descendants).
 */
export function decorateSymbolPickerOptions(
  options: CascaderOptionType[],
  parentPath: string = ""
): CascaderOptionType[] {
  const segments: string[] = columnSegments(options);
  return options.map((option: CascaderOptionType, index: number): CascaderOptionType => {
    const path: string = parentPath === "" ? segments[index] : `${parentPath}${PATH_SEPARATOR}${segments[index]}`;
    const children: CascaderOptionType[] | undefined = Array.isArray(option.children)
      ? decorateSymbolPickerOptions(option.children, path)
      : option.children;
    if (typeof option.label !== "string") {
      return { ...option, children };
    }
    const labelText: string = option.label;
    // An option may carry its own explanatory `title` (e.g. a disabled "Mail Action Link" stating
    // why it's unavailable) — keep it; only fall back to mirroring the label text.
    const title: string = typeof option.title === "string" && option.title !== "" ? option.title : labelText;
    return {
      ...option,
      title,
      label: (
        <SymbolPickerOptionLabel key={labelText} pathTestId={`${SYMBOL_PICKER_OPTION_PATH_TEST_ID_PREFIX}${path}`}>
          {labelText}
        </SymbolPickerOptionLabel>
      ),
      children
    };
  });
}
